import { useEffect, useState, useCallback } from 'react';
import { Select, TextInput } from '@etherisc/ui-kit';
import { getCountryConfig } from '../../config/countries/index';
import { AdministrativeLevel, FarmerAdministrativeLevels } from '../../config/countries/types';
import { getProvinces, getChildren } from '../../services/administrativeUnitService';
import type { AdministrativeUnit } from '../../types/administrativeUnit';

interface AdministrativeLevelsProps {
  countryCode: string;
  values: FarmerAdministrativeLevels;
  onChange: (values: FarmerAdministrativeLevels) => void;
  disabled?: boolean;
  errors?: Record<string, string>;
}

interface LocationOption {
  value: string;
  label: string;
}

export function AdministrativeLevels({ 
  countryCode, 
  values, 
  onChange, 
  disabled = false,
  errors = {}
}: AdministrativeLevelsProps) {
  const [levels, setLevels] = useState<AdministrativeLevel[]>([]);
  const [locationOptions, setLocationOptions] = useState<Record<number, LocationOption[]>>({});
  const [isLoading, setIsLoading] = useState<Record<number, boolean>>({});

  // Load country configuration when country changes
  useEffect(() => {
    const config = getCountryConfig(countryCode);
    if (config) {
      // Filter out country level (level 0) and freetext levels
      const configurableLevels = config.administrativeLevels
        .filter(level => level.level > 0 && level.type === 'predefined')
        .sort((a, b) => a.level - b.level);
      
      setLevels(configurableLevels);
      setLocationOptions({});
      
      // Clear values for levels that don't exist in new country
      const newValues: FarmerAdministrativeLevels = {};
      configurableLevels.forEach(level => {
        if (values[level.level.toString()]) {
          newValues[level.level.toString()] = values[level.level.toString()];
        }
      });
      onChange(newValues);
    }
  }, [countryCode]);

  // Load options for a specific level (progressive hierarchy)
  const loadLevelOptions = useCallback(async (level: AdministrativeLevel, parentCode?: string) => {
    setIsLoading(prev => ({ ...prev, [level.level]: true }));
    
    try {
      let units: AdministrativeUnit[] = [];
      
      if (level.level === 1) {
        // Load provinces (top level)
        units = await getProvinces(countryCode);
      } else if (parentCode) {
        // Load children of selected parent
        units = await getChildren(parentCode);
      }
      
      const options: LocationOption[] = units.map((unit: AdministrativeUnit) => ({
        value: unit.adminCode || '',
        label: unit.adminName,
      })).sort((a, b) => a.label.localeCompare(b.label));
      
      setLocationOptions(prev => ({ ...prev, [level.level]: options }));
    } catch (error) {
      console.error(`Failed to load options for level ${level.level}:`, error);
      setLocationOptions(prev => ({ ...prev, [level.level]: [] }));
    } finally {
      setIsLoading(prev => ({ ...prev, [level.level]: false }));
    }
  }, [countryCode]);



  // Load initial options for provinces (level 1)
  useEffect(() => {
    if (levels.length > 0) {
      const topLevel = levels[0]; // Should be provinces (level 1)
      loadLevelOptions(topLevel);
    }
  }, [levels, loadLevelOptions]);

  // Handle level value change
  const handleLevelChange = useCallback((level: AdministrativeLevel, value: string) => {
    const newValues = { ...values };
    newValues[level.level.toString()] = value;
    
    // Clear dependent levels
    levels.forEach(l => {
      if (l.level > level.level) {
        delete newValues[l.level.toString()];
      }
    });
    
    onChange(newValues);
    
    // Load options for next level if it exists
    const nextLevel = levels.find(l => l.level === level.level + 1);
    if (nextLevel && value) {
      loadLevelOptions(nextLevel, value); // Pass selected value as parent code
    }
  }, [values, onChange, levels, loadLevelOptions]);

  // Load dependent level options when parent values change
  useEffect(() => {
    levels.forEach(level => {
      if (level.level > 1) { // Skip provinces (level 1), they load initially
        const parentLevel = level.level - 1;
        const parentValue = values[parentLevel.toString()];
        if (parentValue && !locationOptions[level.level]) {
          loadLevelOptions(level, parentValue); // Pass parent code for hierarchy
        }
      }
    });
  }, [values, levels, locationOptions, loadLevelOptions]);

  if (levels.length === 0) {
    return null;
  }

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-900">Location Information</h3>
      
      {levels.map(level => {
        const fieldKey = `level_${level.level}`;
        const currentValue = values[level.level.toString()] || '';
        const options = locationOptions[level.level] || [];
        const loading = isLoading[level.level] || false;
        const error = errors[fieldKey];
        
        // Check if this level should be disabled (parent not selected)
        const parentSelected = level.parentLevel === undefined || 
          Boolean(values[level.parentLevel.toString()]);
        
        if (level.type === 'freetext') {
          return (
            <TextInput
              key={level.level}
              label={level.name}
              value={currentValue}
              onChange={(e) => handleLevelChange(level, e.target.value)}
              disabled={disabled || !parentSelected}
              error={error}
              placeholder={`Enter ${level.name.toLowerCase()}`}
            />
          );
        }
        
        return (
          <Select
            key={level.level}
            label={level.name}
            value={currentValue}
            onValueChange={(value) => handleLevelChange(level, value)}
            options={options}
            disabled={disabled || loading || !parentSelected}
            error={error}
            placeholder={
              loading 
                ? `Loading ${level.name.toLowerCase()}...`
                : !parentSelected 
                  ? `Select ${levels.find(l => l.level === level.parentLevel)?.name.toLowerCase()} first`
                  : `Select ${level.name.toLowerCase()}`
            }
          />
        );
      })}
      
      {/* Village field - always freetext for Zimbabwe */}
      <TextInput
        label="Village (Optional)"
        value={values['4'] || ''}
        onChange={(e) => {
          const value = e.target.value;
          const newValues = { ...values };
          if (value) {
            newValues['4'] = value;
          } else {
            delete newValues['4'];
          }
          onChange(newValues);
        }}
        disabled={disabled}
        placeholder="Enter village name"
      />
    </div>
  );
}